import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { IS_URL_REGEX } from "./constants";
import permissions from 'app/models/Permissions';
import Check from 'framework/Check';

// Main Angular Utils File
export default class Utils {
  static wrapDfd<T>(observable: Observable<T>): JQueryPromise<T> {
    return $.Deferred<T>(dfd => {
      observable.subscribe(
        data => dfd.resolve(data),
        err => dfd.reject(err));
    }).promise();
  }

  static distinctBy(key: string, array: any[]): any[] {
    return [...new Map(array.map(item => [item[key], item])).values()];
  }

  static isDate(item: any): boolean {
    return Object.prototype.toString.call(item) === "[object Date]";
  }

  static isDefaultDate(item: any): boolean {
    return moment(item).year() <= 1;
  }

  static getMomentAsUTC(val: any) {
    return moment(moment(val).utc().format('YYYY-MM-DD HH:mm'), 'YYYY-MM-DD HH:mm');
  }

  static getJsDateWithoutTime(val: any) {
    let result: Date = undefined;
    const date = ko.utils.unwrapObservable(val);
    if (moment.isMoment(date) || _.isDate(date)) {
        result = moment(date).startOf('day').toDate();
    }

    return result;
  }

  static getCurrentMomentWithoutTime() {
    return moment(moment().format('L'), 'L');
  }

  static deepFreeze(object: object): object {
    // Retrieve the property names defined on object
    const propNames = Reflect.ownKeys(object as object);

    // Freeze properties before freezing self
    for (const name of propNames) {
      const value = object[name];

      if ((value && typeof value === "object") || typeof value === "function") {
        Utils.deepFreeze(value);
      }
    }

    return Object.freeze(object);
  }

  static showAllMessages(formControl: AbstractControl): void {
    if (formControl instanceof FormControl) {
      formControl.markAsTouched();
      formControl.updateValueAndValidity();
    } else if (formControl instanceof FormGroup) {
      Object.keys(formControl.controls).forEach(key => {
        Utils.showAllMessages(formControl.controls[key]);
      });
    } else if (formControl instanceof FormArray) {
      formControl.controls.forEach(control => {
        Utils.showAllMessages(control);
      });
    }
  }

  static wordCount(value: string): number {
    if (!value || value.trim() === '') {
      return 0;
    }

    let normalizedText = value.
      replace(/(\r\n|\n|\r)/gm, " ").
      replace(/^\s+|\s+$/g, "").
      replace("&nbsp;", " ");

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = normalizedText;

    normalizedText = $(tempDiv).text();

    const words = normalizedText.split(/\s+/);

    return words.filter(x => !x.match(/^([\s\t\r\n]*)$/)).length;
  }

  static filterDuplicates<T>(array: T[], compareFn: (x: T, y: T) => boolean = (x, y) => x === y): T[] {
    return array.filter((value, index, self) => {
      return self.findIndex(item => compareFn(item, value)) === index;
    });
  }

  static partnerInPartnerGroup(partnerId: number, partnerGroup: Boo.Objects.Enums.PartnerGroupEnum, groups: Boo.Objects.Group[]): boolean {
    if (!groups) {
        return false;
    }

    const group = groups.filter(x => x.GroupId === partnerGroup)[0];

    if (!group) {
        return false;
    }

    return group.PartnerGroups.map(x => x.PartnerId).some(x => x === partnerId);
  }

  static selectText(event: MouseEvent) {
    const elem = event.target;
    let range: Range,
        selection: Selection;

    if (window.getSelection) {
      selection = window.getSelection();
      range = document.createRange();
      range.selectNodeContents(elem as Node);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  static escapeRegExp(str: string) {
    if (typeof str === 'string') {
      return str.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&');
    } else {
      return '';
    }
  }

  static containsNoHtml(val: string) {
    return typeof val !== 'string' || !val.match(/<[a-z][\s\S]*>/i);
  }

  static timeFormatFromDuration(duration: number, format?: string) {
    if (!format) {
        format = 'hh:mm:ss';
    }

    duration = Math.abs(duration);
    const seconds = Math.floor(duration / 1000) % 60,
          minutes = Math.floor(duration / (1000 * 60)) % 60,
          hours = Math.floor(duration / (1000 * 60 * 60)) % 24;

    const hourPart = (((hours < 10) ? `0${hours}` : hours.toString())),
          minutePart = (((minutes < 10) ? `0${minutes}` : minutes.toString())),
          secondPart = (((seconds < 10) ? `0${seconds}` : seconds.toString()));
    
    const result = format.replace('hh', hourPart).replace('mm', minutePart).replace('ss', secondPart);

    return result;
  }

  static formatUrl(input: string, returnScheme: boolean, returnSubdomain: boolean, returnPath: boolean, makeSecure?: boolean, noWWW?: boolean) {
    input = $.trim(input).toLowerCase();
    if (!noWWW) {
      input = input.replace('www.', '');
    }

    // Return empty string for falsy input
    if (!input) {
      return '';
    }

    // Add the scheme to the front if it's not there to ensure it's parsed correctly by URI.js
    if (input.indexOf('http') !== 0) {
      if (makeSecure) {
        input = `https://${input}`;
      } else {
        input = `http://${input}`;
      }
    } else {
      if (makeSecure) {
        input = input.replace('http:', 'https:');
      }
    }

    const url = URI(input);

    const path = (returnPath === true ? (url.path() !== '/' ? url.path() : '') : '');
    const subdomain = (returnSubdomain === true ? (url.subdomain() !== '' ? url.subdomain() + '.' : '') : '');
    const scheme = (returnScheme === true ? (url.scheme() !== '' ? url.scheme() + (noWWW ? '://' : '://www.') : '') : '');

    if (url) {
      return scheme + subdomain + url.domain() + path;
    } else if (input.indexOf('http://') >= 0) {
      return input.replace('http://', '');
    } else if (input.indexOf('https://') >= 0) {
      return input.replace('https://', '');
    } else {
      return input;
    }
  }

  static isValidDomain(str: string) {
      // NOT the global domain/url validation. Use `isValidUrl`
      // Used only in the WebsiteDomain component.
      return /^([\u0021-\u003f\u0041-\uffff]+\.)+[\u0021-\u003f\u0041-\uffff]{2,63}$/i.test(str);
  }

  static isValidUrl(str: any) {
      if (!IS_URL_REGEX.test(str)) {
          return false;
      } else {
          return true;
      }
  }

  static parseBoolean(val: boolean | string | number) {
      if (typeof val === 'boolean') {
          return val;
      } else if (typeof val === 'string') {
          if (!isNaN(val as any)) {
              return !!val;
          }

          if (val.trim().length === 0) {
              return false;
          }

          return !(val.trim().toLowerCase() === 'false');
      } else if (typeof val === 'number') {
          return !!val;
      }

      return Boolean(val);
  };

  static getKeywordPhrase(keyword: Boo.Objects.WebsiteUrlKeyword) {
      let phrase = '';

      // if keyword.area != null set phrase to combination of keyword and area.  otherwise set phrase to keyword only
      if (_.isObject(keyword) &&
          _.isObject(keyword.Area) &&
          _.isString(keyword.Area.Name) &&
          _.isObject(keyword.Keyword) &&
          _.isString(keyword.Keyword.Name)) {
          if (keyword.IsAreaFirst) {
              phrase = keyword.Area.Name + ' ' + keyword.Keyword.Name;
          } else {
              phrase = keyword.Keyword.Name + ' ' + keyword.Area.Name;
          }
      } else if (_.isObject(keyword) &&
          _.isObject(keyword.Keyword) &&
          _.isString(keyword.Keyword.Name)) {
          phrase = keyword.Keyword.Name;
      }

      return phrase;
  };

  static hasPermission(partner: Boo.Objects.Partner, partnerUsers: Boo.Objects.PartnerUser[], permission: permissions, user?: Boo.Objects.User) {
      if (!partner || !partnerUsers || !permission) {
          return false;
      }

      // filter partner users by partner
      const filteredPartnerUsers = partnerUsers.filter(p => p.PartnerId === partner.PartnerId);

      // get user's user levels.
      const userLevels = filteredPartnerUsers.map(x => x.UserLevelId);

      // if the user has a permission that matches one of their user levels.
      const hasPartnerUserLevelPermission = partner.PartnerUserLevelPermissions.some(p => userLevels.includes(p.UserLevelId) && p.PermissionId === permission);

      const hasUserLevelPermission = user &&
          user.UserPermissions &&
          user.UserPermissions.some(p => p.PermissionId === permission);

      return hasPartnerUserLevelPermission || hasUserLevelPermission;
  };

  static canAccessInternalTasks(partnerUsers: Boo.Objects.PartnerUser[]) {
      let result = false;
      for (const partnerUser of partnerUsers) {
        if (partnerUser.TaskTypes) {
          const internalTaskTypeIds = [
              Boo.Objects.Enums.TaskTypeEnum.InternalTaskQaClassifiedBusinessListings,
              Boo.Objects.Enums.TaskTypeEnum.InternalTaskQaLocalBusinessCitations,
              Boo.Objects.Enums.TaskTypeEnum.InternalTaskQaOnsiteBloggingReview,
              Boo.Objects.Enums.TaskTypeEnum.InternalTaskQaOnsiteCopyReview,
              Boo.Objects.Enums.TaskTypeEnum.InternalTaskQaPremiumMonthlyCampaignUpdate,
              Boo.Objects.Enums.TaskTypeEnum.InternalTaskQaPremiumPreliminaryAudit,
              Boo.Objects.Enums.TaskTypeEnum.InternalTaskQaProactiveUnderperformingKeyword,
              Boo.Objects.Enums.TaskTypeEnum.InternalTaskQaPremiumBacklink,
              Boo.Objects.Enums.TaskTypeEnum.InternalTaskQaOnsiteRecommendationReview
          ];

          for (const taskType of partnerUser.TaskTypes) {
              if (!result) {
                  result = internalTaskTypeIds.includes(taskType.TaskTypeEnum);
              }
          }
        }
      }

      return result;
  }

  static log(...args) {
      if (window.testConfig.logToConsole) {
          if (arguments.length > 1) {
              console.log(args.join(' -> '));
          } else {
              console.log(args[0]);
          }
      }
  }

  static parseCsv(file: File, expectedFormat: string[]): Observable<string[]> {
    return new Observable<string[]>((subscriber) => {
      const fileReader = new FileReader();

      fileReader.onload = (e) => {
        const result: PapaParse.ParseResult = Papa.parse(fileReader.result.toString(), { skipEmptyLines: true });

        try {
          Check.isNotNull(result, 'Cannot read CSV file');
          Check.isCsvFormatValidRobust(expectedFormat, result, true); // requireAllValues
        }
        catch (err) {
          subscriber.error(err);
          subscriber.complete();
          return;
        }

        subscriber.next(result.data.slice(1)); // Remove the first (header) row
        subscriber.complete();
      };

      fileReader.readAsText(file);
    });
  }
}
